import { __, apply, compose, equals, trim } from 'ramda';
import { pad2Zeros } from './string.helpers';

const objectToString = Object.prototype.toString;
const objectToStringCall = objectToString.call.bind(objectToString);

// Any -> Boolean
export const isDate = compose(equals('[object Date]'), objectToStringCall);

//  XML <-> Date conversions
const parseXmlDate = function (strDate) {
    var parts = strDate.split('-');

    if (parts.length !== 3) {
        return null;
    }

    var month = parts[1] - 1;

    return month >= 0 && month <= 11
        ? new Date(+parts[0], month, +parts[2])
        : null;
};

const parseXmlTime = function (strTime) {
    var parts = strTime.split(':');

    if (parts.length === 3) {
        //return new Date(1000 * (parts[0] * 3600 + parts[1] * 60 + (+parts[2])));
        return new Date(1970, 0, 1, parts[0], parts[1], parts[2]);
    }

    return null;
};

function makeDate(utc, y, m, d, h, mm, s, ms) {
    return utc
        ? Date.UTC(y, m, d, h || 0, mm || 0, s || 0, ms || 0)
        : new Date(y, m, d, h || 0, mm || 0, s || 0, ms || 0);
}

export function parseXmlDate2(value, options) {
    if (!value) {
        return null;
    }
    var isoOnly = options && options.isoOnly;
    var utc = !(options && options.mode === 'local');

    value = trim(value);

    // 0 - full str
    // 1 - year
    // 2 - month
    // 3 - day
    // 4 - time
    // 5 - hours
    // 6 - mins
    // 7 - seconds
    // 8 - offset
    // 9 - Z
    var dateRegex = isoOnly
        ? /^(\d\d\d\d)-(\d\d)-(\d\d)(T(\d\d):(\d\d)(?::(\d\d(?:\.\d+)?))?)?([+-]\d{0,4})?([zZ]?)$/
        : /^(\d\d\d\d)-(\d\d)-(\d\d)([ |T]?(\d\d):(\d\d)(?::(\d\d(?:\.\d+)?))?)?([+-]\d{0,4})?([zZ]?)$/;
    var parts = dateRegex.exec(value);

    if (!parts) {
        return null;
    }

    var containsTime = !!parts[4];
    var year = Number(parts[1]);
    var month = Number(parts[2]) - 1; // JavaScript months are 0-indexed
    var day = Number(parts[3]);
    var hour = Number(parts[5]);
    var minutes = Number(parts[6]);

    var seconds = parts[7] ? Number(parts[7]) : 0;
    var fullSeconds = Math.floor(seconds);
    var milliseconds = Math.floor((seconds - fullSeconds) * 1000);

    var timeStamp = containsTime
        ? makeDate(
              utc,
              year,
              month,
              day,
              hour,
              minutes,
              fullSeconds,
              milliseconds,
          )
        : makeDate(utc, year, month, day);

    return {
        date: new Date(timeStamp),
        offset: parts[8] || null,
        z: !!parts[9],
    };
}

// "parseIsoDate" is deprecated. use "parseXmlDate" instead
export function parseIsoDate(dateStr) {
    return parseXmlDate2(dateStr, { isoOnly: true });
}

export function makeIsoDate(dateSpec) {
    var isoStr = dateSpec.date.toISOString().replace(/[zZ]$/, '');
    var isoStrMs = dateSpec.omitMilliseconds
        ? isoStr.replace(/\.\d{3}$/, '')
        : isoStr;

    var datePart = dateSpec.omitTimeIfZero
        ? isoStrMs.replace(/T00:00:00(\.000)?$/, '')
        : isoStrMs;
    var offsetPart = dateSpec.offset || '';
    var zPart = dateSpec.z ? 'Z' : '';

    return [datePart, offsetPart, zPart].join('');
}

function getDateMethodPrefix(options) {
    var utc = options && options.mode === 'utc';

    return utc ? 'getUTC' : 'get';
}

// (Date, { mode: String = 'local' }) -> String
export const getXmlDate = function (dt, options) {
    var prefix = getDateMethodPrefix(options);

    var year = dt[prefix + 'FullYear']();
    var month = dt[prefix + 'Month']() + 1;
    var day = dt[prefix + 'Date']();

    return [year, pad2Zeros(month), pad2Zeros(day)].join('-');
};

// (Date, { mode: String = 'local' }) -> String
export const getXmlTime = function (dt, options) {
    var prefix = getDateMethodPrefix(options);

    var hours = dt[prefix + 'Hours']();
    var minutes = dt[prefix + 'Minutes']();
    var seconds = dt[prefix + 'Seconds']();

    return [hours, minutes, seconds].map(pad2Zeros).join(':');
};

// (Date, { mode: String = 'local', separator: String = 'T' }) -> String
export const getXmlDateTime = function (dt, options) {
    return [getXmlDate, getXmlTime]
        .map(apply(__, [dt, options]))
        .join((options && options.separator) || 'T');
};

export function mergeDateValue(oldValue, isValidDate, value, isDateTime) {
    if (oldValue && !oldValue.endsWith('Z')) {
        oldValue += 'Z';
    }

    var oldDate =
        oldValue && oldValue.length > 11 && !Number.isNaN(Date.parse(oldValue))
            ? parseIsoDate(oldValue)
            : null;
    var selectedDate = isValidDate && value ? parseIsoDate(value) : null;

    if (selectedDate) {
        selectedDate.omitMilliseconds = true;

        if (oldDate) {
            // case 42803
            selectedDate.date = new Date(
                Date.UTC(
                    selectedDate.date.getUTCFullYear(),
                    selectedDate.date.getUTCMonth(),
                    selectedDate.date.getUTCDate(),
                    oldDate.date.getUTCHours(),
                    oldDate.date.getUTCMinutes(),
                    oldDate.date.getUTCSeconds(),
                ),
            );

            return makeIsoDate(selectedDate);
        }
        selectedDate.omitTimeIfZero = !isDateTime; //omit only if date field

        return makeIsoDate(selectedDate);
    }

    return value;
}

export function mergeTimeValue(oldValue, value) {
    var currentDate = new Date().toDateString(),
        oldDateMilli = oldValue ? Date.parse(oldValue) : null,
        oldDate =
            oldDateMilli && !Number.isNaN(oldDateMilli) && oldDateMilli > 0
                ? new Date(oldValue)
                : null,
        timeRegExp = /^([0]?[0-9]|1[0-9]|2[0-3]):([0-5]\d):([0-5]\d)\s?$/gim;

    if (oldDate) {
        oldDate = new Date(
            oldDate.getTime() + oldDate.getTimezoneOffset() * 60 * 1000,
        );
    }

    var isPMValue =
        /^([0]?[0-9]|1[0-9]|2[0-3]):([0-5]\d):([0-5]\d)(\s+PM)\s?$/gim.test(
            value,
        );
    var timeValue = value;

    if (value) {
        value = value.replace(/\s+(AM|PM)/gi, '');
    }

    if (value && timeRegExp.test(value)) {
        var valueSubstr = value,
            selectedDateMilli = Date.parse(currentDate + ' ' + valueSubstr),
            selectedDate;

        if (!Number.isNaN(selectedDateMilli)) {
            selectedDate = new Date(selectedDateMilli);

            if (selectedDate.getHours() === 12) {
                selectedDate.setHours(0);
            }

            if (isPMValue) {
                selectedDate.setHours(selectedDate.getHours() + 12);
            }
        }

        if (selectedDate) {
            if (oldDate) {
                selectedDate.setFullYear(oldDate.getFullYear());
                selectedDate.setMonth(oldDate.getMonth());
                selectedDate.setDate(oldDate.getDate());
            }
            selectedDate = new Date(
                selectedDate.getTime() -
                    selectedDate.getTimezoneOffset() * 60 * 1000,
            );

            timeValue = selectedDate.toISOString().substr(0, 19);
        }
    }

    return timeValue;
}

// Note: The dates parsed by this method are parsed as a LOCAL time: 2017-10-10T11:11:11 will be parsed as 11:11:11 local time, Oct. 10, 2017
export const parseXmlDateTime = function (strDateTime) {
    const [datePart, timePart] = strDateTime.split('T');
    let date = parseXmlDate(datePart);

    if (date && timePart) {
        const time = parseXmlTime(timePart);

        date.setHours(time.getHours());
        date.setMinutes(time.getMinutes());
        date.setSeconds(time.getSeconds());
        date.setMilliseconds(time.getMilliseconds());
    }

    if (!date || Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

export const makeLocaleDateTime = (date) =>
    date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
